import { usePosts } from '@gik/api/posts/posts';
import { useSavedArticleIds } from '@gik/api/users/savedArticles';
import { removeFavoriteArticle } from '@gik/api/users/user';
import type Article from '@gik/blog/models/Article';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import routes from '@gik/core/routes';
import { useUserStore } from '@gik/core/store/UserStore';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { ArticleTile } from '@gik/ui-legacy/gik/ArticleTile';
import SavedItems from '@gik/user-profile/components/SavedItems/SavedItems';
import { SavedItemsGrid } from '@gik/user-profile/components/SavedItemsGrid/SavedItemsGrid';
import HeartIcon from '@heroicons/react/outline/HeartIcon';
import NewspaperIcon from '@heroicons/react/outline/NewspaperIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SavedItemsEmpty } from '../SavedItemsEmpty/SavedItemsEmpty';
import { translationKeys } from './i18n/en';

export function SavedArticlesSectionComp() {
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);

  const requestKeyIds = `user-${userId}-saved-article-ids`;
  const requestKeyItems = `user-${userId}-saved-articles`;

  return (
    <SavedItems
      sectionTitle={t(translationKeys.title)}
      buttonTitle={t(translationKeys.btnSeeAll)}
      buttonPrepend={<SvgIcon Icon={NewspaperIcon} />}
      buttonLink={routes.blog}
    >
      <SavedItemsGrid<WordpressPost, Article>
        itemName={'article'}
        optimisticIdsComparer={article => articleId => articleId !== article.articleId}
        optimisticItemsComparer={article => item => item.id.toString() != article.articleId}
        removeCall={async article => await removeFavoriteArticle(userId, article.articleId)}
        skeleton={<ArticleTile skeleton layout={'row'} />}
        emptyView={
          <SavedItemsEmpty>
            Click the <SvgIcon Icon={HeartIcon} /> on Articles to save them here.
          </SavedItemsEmpty>
        }
        requestKeyIds={requestKeyIds}
        requestKeyItems={requestKeyItems}
        fetchIds={function useFetch() {
          return useSavedArticleIds(userId, { requestKey: requestKeyIds });
        }}
        fetchItems={function useFetch(ids) {
          return usePosts({ ids }, { requestKey: requestKeyItems });
        }}
        transformItems={posts => posts.map(WordpressConverter.convertPost)}
        tileMapper={(article, onClickRemove) => (
          <ArticleTile key={article.articleId} article={article} onClickRemove={onClickRemove} layout={'row'} />
        )}
      />
    </SavedItems>
  );
}

export const SavedArticlesSection = withComponentErrorBoundary(SavedArticlesSectionComp);
